import styled, { css } from 'styled-components';
import Eyebrow from '@klv/atoms/eyebrow';
import Typography from '@klv/atoms/typography';
import Buttons from '@klv/molecules/buttons';
import Grid from '@klv/utils/grid';
import { backgroundToTextColor } from '@klv/shared/styles/themes/utils';
import { InHeroContainer } from '@klv/organisms/form/styles';

import type { IHeadlineFont } from '@klv/atoms/headline-font/types';
import type { IHeroGridVariant, IHeroGridVertical, IHeroWrapper, THeroImageTypes } from '../types';

export const ContentContainer = styled.div<{ $hasMedia: boolean; }>`
  ${({ theme }) => theme.globalPadding};
  padding-bottom: ${({ $hasMedia }) => (!$hasMedia ? '0' : '')};
`;

export const HeroBodyCopy = styled(Typography)``;

export const HeroEyebrow = styled(Eyebrow)`
  color: inherit;
  margin: 0 0 1.6rem;

  ${({ theme }) => theme.screenSize.s768.mediaQuery} {
    margin-bottom: 2.4rem;
  }
`;

export const HeroHeadline = styled(Typography) <{ $fontFamily: IHeadlineFont['fontFamily']; }>`
  & + ${HeroBodyCopy} {
    margin-top: 1.6rem;

    ${({ theme }) => theme.screenSize.s768.mediaQuery} {
      margin-top: 2rem;
    }
  }

  ${({ $fontFamily, theme }) => `
    font-family: var(--font-family-${$fontFamily});
    ${($fontFamily === 'sohne-schmal') ? `
      font-size: 7.2rem;
      line-height: 100%;
      letter-spacing: 0.01em;
      text-transform: uppercase;

      ${theme.screenSize.s768.mediaQuery} {
        font-size: 12.8rem;
      }
    ` : ''}
  `}
`;

export const MediaContainer = styled.div<{ $imageType: THeroImageTypes; }>`
  align-self: center;

  .gatsby-image-wrapper {
    height: 100%;
    width: 100%;
  }
`;

export const StyledButtons = styled(Buttons)`
  margin-top: 3.2rem;
`;

export const StyledHero = styled.div<IHeroWrapper>`
  ${({ $background, $backgroundJpeg, $isTwoColumn, $pricingVariant, theme }) => `
    color: ${backgroundToTextColor[$background as keyof typeof backgroundToTextColor]};
    margin-top: calc(var(--layout-universal-navbar-height) * -1);
    ${theme.globalPadding};
    padding-top: var(--layout-universal-navbar-height);
    position: relative;
    overflow: hidden;
    ${$backgroundJpeg ? `
      &:after {
        z-index: -2;
        content: '';
        position: absolute;
        left:0;
        top: 0;
        right: 0;
        bottom: 0;
        background-image: url(/static/hero-gradients/${$backgroundJpeg}${$isTwoColumn ? '2Col' : ''}.webp), linear-gradient(${theme.colors[$background]}, ${theme.colors[$background]});
        background-repeat: repeat;
        background-size: ${$isTwoColumn ? '3500px' : '2500px'}, 100% 100%;
        background-position: center top;
        ${theme.screenSize.s768.mediaQuery} {
          background-size: 3500px;
        }
      }
      &:before {
        z-index: -1;
        content: '';
        position: absolute;
        left:0;
        top: 0;
        right: 0;
        bottom: 0;
        background-image: url(/static/hero-gradients/noise.svg), url(/static/hero-gradients/noise.svg);
        background-size: 700px, 350px;
        mix-blend-mode: overlay;
        backdrop-filter: blur(20px);
      }
      ` : `
      background-color: ${theme.colors[$background]};
    `}

    ${$pricingVariant ? `
      padding-bottom: 2.4rem;

      ${ContentContainer} {
        padding-top: 4rem;
      }

      ${HeroBodyCopy} {
        margin-top: 1.6rem;
      }

      ${theme.screenSize.s1024.mediaQuery} {
        padding-bottom: 4.8rem;

        ${ContentContainer} {
          padding-top: 4.8rem;
        }

        ${HeroBodyCopy} {
          margin-top: 2.4rem;
        }
      }
    ` : ''}
  `}
`;

export const TextHeroGrid = styled(Grid) <IHeroGridVariant>`
  ${ContentContainer} {
    grid-column: col1 / col8;
    text-align: ${({ textAlignment }) => textAlignment};
    ${({ theme }) => theme.screenSize.s576.mediaQuery} {
      grid-column: ${({ textAlignment }) => textAlignment === 'center' ? 'col3 / col10' : 'col1 / col12'};
    }
  }
`;

export const HorizontalHeroGrid = styled(Grid) <IHeroGridVariant>`
  grid-template-rows: repeat(2, auto);
  text-align: ${({ textAlignment }) => textAlignment};
  ${ContentContainer},
  ${MediaContainer} {
    grid-column: col1 / col8;

    ${({ theme }) => theme.screenSize.s576.mediaQuery} {
      grid-column: ${({ textAlignment }) => textAlignment === 'center' ? 'col3 / col10' : 'col1 / col12'};
    }
  }

  ${MediaContainer} {
    ${({ theme }) => `
      ${theme.screenSize.s380.mediaQuery} {
        margin-left: -${theme.screenSize.s380.gridHorizontalPadding}rem;
        margin-right: -${theme.screenSize.s380.gridHorizontalPadding}rem;
      }
      ${theme.screenSize.s576.mediaQuery} {
        margin-left: -${theme.screenSize.s576.gridHorizontalPadding}rem;
        margin-right: -${theme.screenSize.s576.gridHorizontalPadding}rem;
      }
      ${theme.screenSize.s768.mediaQuery} {
        margin-left: -${theme.screenSize.s768.gridHorizontalPadding}rem;
        margin-right: -${theme.screenSize.s768.gridHorizontalPadding}rem;
      }
      ${theme.screenSize.s992.mediaQuery} {
        margin-left: -${theme.screenSize.s992.gridHorizontalPadding}rem;
        margin-right: -${theme.screenSize.s992.gridHorizontalPadding}rem;
      }
      ${theme.screenSize.s1024.mediaQuery} {
        margin-left: -${theme.screenSize.s1024.gridHorizontalPadding}rem;
        margin-right: -${theme.screenSize.s1024.gridHorizontalPadding}rem;
      }
      ${theme.screenSize.s1200.mediaQuery} {
        margin-left: -${theme.screenSize.s1200.gridHorizontalPadding}rem;
        margin-right: -${theme.screenSize.s1200.gridHorizontalPadding}rem;
      }
      ${theme.screenSize.s1366.mediaQuery} {
        margin-left: -${theme.screenSize.s1366.gridHorizontalPadding}rem;
        margin-right: -${theme.screenSize.s1366.gridHorizontalPadding}rem;
      }
    `}
  }
`;

export const HasEmbeddedForm = styled.div``;

export const VerticalHeroGrid = styled(Grid) <IHeroGridVertical>`
  ${({ theme, hasForm, embeddedForm }) => css`
    grid-template-rows: min-content auto;

    ${embeddedForm === 'none' ? css`
      ${ContentContainer},
      ${MediaContainer},
      .modalTrigger {
        grid-column: col1 / col8;

        ${theme.screenSize.s576.mediaQuery} {
          grid-column: col1 / col12;
        }
      }

      ${ContentContainer} {
        ${theme.screenSize.s576.mediaQuery} {
          grid-column: col1 / col12;
        }

        ${theme.screenSize.s1024.mediaQuery} {
          grid-column: col1 / col5;
          padding-bottom: 4rem;
          padding-top: 7rem;
        }
      }
      ${MediaContainer},
      .modalTrigger {
        grid-row: ${(hasForm ? '1' : '2')};
        max-width: ${(hasForm ? 'none' : '67.2rem')};
        ${theme.screenSize.s1024.mediaQuery} {
          grid-column: col7 / col12;
          grid-row: unset;
          margin-right: ${`-${theme.screenSize.s1024.gridHorizontalPadding}rem`};
        }
        ${theme.screenSize.s1200.mediaQuery} {
          margin-right: ${`-${theme.screenSize.s1200.gridHorizontalPadding}rem`};
        }
        ${theme.screenSize.s1366.mediaQuery} {
          margin-right: ${`-${theme.screenSize.s1366.gridHorizontalPadding}rem`};
        }
      }
      .modalTrigger {
        max-width: none;
        ${MediaContainer} {
          margin: 0;
          max-width: none;
        }
      }
    ` : ''}

    ${embeddedForm !== 'none' ? css`
      ${HasEmbeddedForm} {
        grid-column: col1 / col12;
        ${theme.screenSize.s768.mediaQuery} {
          grid-column: col1 / col6;
        }
      }

      ${InHeroContainer} {
        ${theme.globalPadding};
        grid-column: col1 / col12;
        ${theme.screenSize.s768.mediaQuery} {
          grid-column: col8 / col12;
        }
      } 
    ` : ''}
  `}  
`;
